<template>
  <div
    :class="[
      { 'tw-mb-0': !path && !title }
    ]"
    class="tw-rounded-xl tw-bg-white tw-transition-all"
  >
    <NuxtLink
      :to="path"
      :title="title"
      class="tw-block tw-w-full"
    >
      <div class="tw-flex tw-flex-col" @click="$emit('click')">
        <!-- Image Section -->
        <div
          class="
            tw-relative tw-overflow-hidden tw-rounded-xl
            tw-mb-4 hover:-tw-translate-y-2 tw-duration-500
          "
        >
          <MediaImageLazyLoad
            :src="image?.medium || image?.large || ''"
            :placeholder-src="image?.tiny || ''"
            :alt="title"
            :ratio="1/1"
            class="tw-rounded-xl"
            seo-optimize
          />
          <BadgeRecommended
            v-if="recommended"
          />
        </div>

        <!-- Category -->
        <div
          v-if="category.path && category.title"
          class="tw-text-secondary tw-text-mb-body-small lg:tw-text-dt-body-small tw-truncate"
        >
          {{ category.title }}
        </div>

        <!-- Title -->
        <div v-if="path && title">
          <component
            :is="titleTag"
            class="tw-text-[#282828] tw-text-mb-body lg:tw-text-dt-body tw-line-clamp-2 tw-mb-1 tw-min-h-[2lh]"
          >
            {{ title }}
          </component>
        </div>

        <DisplayPrice
          :options="options"
          :show-options-divider="false"
          :show-option-list="showOptionList"
          :min-price-option="minPriceOption"
          :max-price-option="maxPriceOption"
          :min-price-of-discounted-option="minPriceOfDiscountedOption"
          :max-price-of-discounted-option="maxPriceOfDiscountedOption"
          :price="price"
          :price-of-discounted="priceOfDiscounted"
        />
      </div>
    </NuxtLink>
  </div>
</template>

<script setup lang="ts">
defineProps({
  image: {
    type: Object as PropType<ImageMeta>,
    required: true,
  },
  path: {
    type: String,
    default: '',
  },
  title: {
    type: String,
    default: '',
  },
  options: {
    type: Array as PropType<ProductOption[]>,
    default: () => [],
  },
  price: {
    type: Number,
    default: 0,
  },
  showOptionList: {
    type: Boolean,
    default: true,
  },
  priceOfDiscounted: {
    type: Number,
    default: 0,
  },
  maxPriceOption: {
    type: Number,
    default: 0,
  },
  minPriceOption: {
    type: Number,
    default: 0,
  },
  minPriceOfDiscountedOption: {
    type: Number,
    default: 0,
  },
  maxPriceOfDiscountedOption: {
    type: Number,
    default: 0,
  },
  recommended: {
    type: Boolean,
    default: false,
  },
  category: {
    type: Object as PropType<Category>,
    default: () => ({ title: '', path: '' }),
  },
  titleTag: {
    type: String,
    default: 'h3',
  },
})

// Emits
defineEmits(['click'])
</script>

<style scoped>
@media (min-width: 1024px) {
  :deep(.tw-text-danger-toast) {
    font-size: var(--theme-font-text-dt-title-5-size);  
    line-height: var(--theme-font-text-dt-title-5-line-height);  
    font-weight: var(--theme-font-text-dt-title-5-weight);
  }

  :deep(.tw-line-through) {
    font-size: var(--theme-font-text-dt-body-small-size);  
    line-height: var(--theme-font-text-dt-body-small-line-height);  
    font-weight: var(--theme-font-text-dt-body-small-weight);  
  }
}

@media (max-width: 1023px) {
    :deep(.tw-text-danger-toast) {
    font-size: var(--theme-font-text-mb-title-5-size);  
    line-height: var(--theme-font-text-mb-title-5-line-height);  
    font-weight: var(--theme-font-text-mb-title-5-weight);  
  }

  :deep(.tw-line-through) {
    font-size: var(--theme-font-text-mb-body-tiny-size);  
    line-height: var(--theme-font-text-mb-body-tiny-line-height);  
    font-weight: var(--theme-font-text-mb-body-tiny-weight);  
  }
}
</style>
